import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { debounceTime, finalize, firstValueFrom, of, Subscription, switchMap, take } from 'rxjs';
import { UtilsService } from '../../services/utils.service';
import { UsersService } from '../../services/entities/users/users.service';
import { AuthService } from '../../services/auth.service';
import { IUserRegistrationPostData } from '../../models/user/user-registration-post-data.model';
import { IOrgUser, IUser } from '../../models/user/user.model';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Language } from '../../enums/utils/languages.enum';
import { emailValidator } from '../../utils/session-group-form-utils/validators';
import { UserHelperService } from '../../services/helpers/user-helper.service';

export enum AuthComponentState {
  INIT = 'INIT',
  SIGN_IN = 'SIGIN_IN',
  SIGN_UP = 'SIGN_UP',
  SIGN_UP_SUCCESS = 'SIGN_UP_SUCCESS'
};

interface IAuthForm {
  email?: FormControl<string>,
  password?: FormControl<string>,
  name?: FormControl<string>,
  surname?: FormControl<string>
};

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, TranslateModule,
    ButtonModule, MessageModule, InputTextModule
  ],
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss']
})
export class AuthComponent implements OnInit, OnDestroy {
  AuthComponentState = AuthComponentState;

  @Input() state: AuthComponentState | undefined;
  @Input() initEmail: string | undefined;
  @Input() unregisteredUser: (IUser & { orgUsers: IOrgUser[]; }) | undefined;
  @Input() setRedirectAfterLoad: boolean | undefined = false;
  @Input() magicCodeUuid: string | undefined;
  @Output() signedIn: EventEmitter<boolean> = new EventEmitter();


  form = new FormGroup<IAuthForm>({
  });
  forgottenPasswordFG = new FormGroup({
    email: new FormControl(null, [ Validators.required, emailValidator ])
  });
  emailSub: Subscription = Subscription.EMPTY;

  checkingEmail: boolean = false;
  loading: boolean = false;
  sendingResetPasswordEmail: boolean = false;
  showForgottenPassword: boolean = false;

  constructor(
    private utilsService: UtilsService,
    private usersService: UsersService,
    private authService: AuthService,
    private translate: TranslateService,
    private uHelper: UserHelperService
  ) { }

  ngOnInit(): void {
    this.changeState(this.state ?? AuthComponentState.INIT);

  }

  onSubmit() {
    this.utilsService.markFormGroupDirty(this.form);

    if (this.form.invalid || this.loading || (this.checkingEmail && this.state !== AuthComponentState.SIGN_IN )) {
      console.error('invalid');
      return;
    }

    this.authService.redirectToDashboardAfterUserLoad = !!this.setRedirectAfterLoad;

    const formValues = this.form.getRawValue();
    const email = formValues.email!.toLowerCase().trim();

    this.loading = true;
    switch (this.state) {
      case AuthComponentState.INIT:
        this.usersService.doesRegisteredUserExistByEmail(this.emailFC.value)
        break;
      case AuthComponentState.SIGN_IN:
        this.authService.signIn(email, formValues.password!).then(() => {
          this.signedIn.emit(true);
        }).finally(() => {
          // this.loading = false; nechat .. protoze chvili trva nez se nacte user a pak se to samo presmeruje
        }).catch(() => {
          this.loading = false;
        });
        break;
      case AuthComponentState.SIGN_UP:
        const data: IUserRegistrationPostData = {
          email: email,
          name: formValues.name!,
          surname: formValues.surname!,
          magicCodeUuid: this.magicCodeUuid,
          preferredLanguage: this.translate.currentLang as Language
        };
        this.usersService.postRegistration(data).pipe(
          take(1),
          finalize(() => this.loading = false)
        ).subscribe({
          next: (res) => {
            if (res.signInToken) {
              this.authService.signInWithToken(res.signInToken).then(() => {
                this.signedIn.emit(true);
              })
            } else {
              this.changeState(AuthComponentState.SIGN_IN);
            }
          }
        });
        break;
      default:
        this.utilsService.logError(`State not handled! (${this.state}) 1uoi2123312k`);
        break;
    }
  }

  onSubmitPasswordReset() {
    this.utilsService.markFormGroupDirty(this.forgottenPasswordFG);
    if (this.forgottenPasswordFG.invalid) {
      console.error('invalid');
      return;
    }

    this.sendingResetPasswordEmail = true;
    this.authService.sendPasswordResetEmail(this.forgottenPasswordFG.value.email!)
      .finally(() => {
        this.sendingResetPasswordEmail = false;
      }).then(() => {
        this.showForgottenPassword = false;
      });
  }

  changeState(state: AuthComponentState) {
    this.removeControls();
    this.state = state;
    switch (state) {
      case AuthComponentState.INIT:
        this.addEmailFC();
        break;
      case AuthComponentState.SIGN_IN:
        this.addEmailFC();
        this.addPasswordFC();

        break;
      case AuthComponentState.SIGN_UP:
        this.addEmailFC();
        this.addNameSurnameFC();

        break;
      default:
        this.utilsService.logError('State not handled! 1i23jl12k31');
        break;
    }
  }

  addEmailFC() {
    const emailFC = new FormControl((this.initEmail || this.unregisteredUser?.email) ?? '', { nonNullable: true, validators: [ Validators.required, emailValidator ]});
    if (this.initEmail) emailFC.disable();
    if (!this.emailFC) {
      this.form.addControl('email', emailFC);
      this.watchEmail();
    }
  }
  addPasswordFC() {
    const passwordFC = new FormControl('', { nonNullable: true, validators: [ Validators.required ]});
    if (!this.passwordFC) {
      this.form.addControl('password', passwordFC);
    }
  }
  addNameSurnameFC() {
    // TODO: org-user
    const nameObj = this.unregisteredUser ? this.uHelper.getOrgUserObj(this.unregisteredUser.orgUsers, null) : null;
    const nameFC = new FormControl(nameObj?.name ? nameObj.name : '', { nonNullable: true, validators: [ Validators.required ] });
    const surnameFC = new FormControl(nameObj?.surname ? nameObj.surname : '', { nonNullable: true, validators: [ Validators.required ] });
    if (!this.nameFC) this.form.addControl('name', nameFC);
    if (!this.surnameFC) this.form.addControl('surname', surnameFC);
  }

  removeControls() {
    this.form.removeControl('password');
    this.form.removeControl('name');
    this.form.removeControl('surname');
  }

  onForgottenPassword() {
    this.showForgottenPassword = !this.showForgottenPassword;


  }

  watchEmail() {

    this.emailSub = this.emailFC.valueChanges
      .pipe(
        debounceTime(300),
        switchMap(x => {
          if (!x || this.emailFC.invalid) return of();
          this.checkingEmail = true;
          return firstValueFrom(this.usersService.doesRegisteredUserExistByEmail(x));
        })
      ).subscribe(async resPromise => {
        const res = await resPromise;
        if (res) {
          if (this.state !== AuthComponentState.SIGN_IN) this.changeState(AuthComponentState.SIGN_IN);
        } else {
          this.changeState(AuthComponentState.SIGN_UP);
        }
        this.checkingEmail = false;
      });

  }

  get emailFC() {
    return this.form.get('email') as FormControl<string>;
  }
  get passwordFC() {
    return this.form.get('password') as FormControl<string>;
  }
  get nameFC() {
    return this.form.get('name') as FormControl<string>;
  }
  get surnameFC() {
    return this.form.get('surname') as FormControl<string>;
  }

  ngOnDestroy(): void {
    this.emailSub.unsubscribe();
  }

}
